import { AppAreaConfig, DomainConfig } from './@types'
import { PaywallRoute } from '@/@types/router-types'
import { APP_AREAS } from '@/funnels/areas'
import { ENTRY_POINTS } from '@/funnels/entryPoints'

const paywallRouteToArea = new Map<string, string>()
for (const area of APP_AREAS) {
	if (area.paywall?.routeName) {
		paywallRouteToArea.set(area.paywall.routeName, area.appArea)
	}
	if (area.paywall?.overrides) {
		for (const override of area.paywall.overrides) {
			if (override.routeName) {
				paywallRouteToArea.set(override.routeName, area.appArea)
			}
		}
	}
}

export const resolveAppAreaByPaywallRoute = (routeName: string): string | null => {
	return paywallRouteToArea.get(routeName) ?? null
}

// Runtime type guard
export const isAppAreaConfig = (config: unknown): config is AppAreaConfig => {
	return (
		typeof config === 'object' && config !== null && 'appArea' in config && 'landing' in config && 'funnel' in config
	)
}

// Default values from main funnel for redirect funnels fallbacks — the LIVE paywall of the main
// (glp-app) area, not the retired per-medication one (DEV-35774). No-op today: both areas declare
// their own `paywall`.
const DEFAULT_PAYWALL_CONFIG: { routeName: PaywallRoute } = {
	routeName: 'plan-glp-app',
}

// Generic helper to get config value with fallback
const getConfigValue = <T extends object, K extends keyof T>(obj: T | undefined, key: K, defaultValue: T[K]): T[K] => {
	return obj && key in obj ? obj[key] : defaultValue
}

// Helper functions to get config values with fallbacks
export const getPaywallRouteName = (config: AppAreaConfig): string =>
	getConfigValue(config.paywall, 'routeName', DEFAULT_PAYWALL_CONFIG.routeName)

// Route generation functions
function generateRoutesFromAppArea(appAreaConfig: AppAreaConfig) {
	const routes: Array<{
		path: string
		name: string
		component: () => Promise<unknown>
		meta?: { isLanding?: boolean }
	}> = []

	if (appAreaConfig.landing?.route) {
		routes.push({
			path: appAreaConfig.landing.route.path,
			name: appAreaConfig.landing.route.name,
			component: () => import('@/modules/Landing/LandingView.vue'),
			meta: {
				isLanding: true,
			},
		})
	}

	if (appAreaConfig.funnel?.route) {
		routes.push({
			path: appAreaConfig.funnel.route.path,
			name: appAreaConfig.funnel.route.name,
			component: () => import(/* webpackChunkName: "ob-survey" */ '@/modules/Onboarding/OnboardingView.vue'),
		})
	}

	return routes
}

export function generateDomainConfig(
	appAreas: AppAreaConfig[],
	manualPathnames?: { [pathname: string]: AppAreaConfig },
): DomainConfig {
	const config: DomainConfig = { ...manualPathnames }

	for (const appArea of appAreas) {
		if (appArea.landing?.route?.path) {
			const pathname = appArea.landing.route.path
			config[pathname] = appArea
		}
	}

	return config
}

export function funnelRoutes(domainPathnames?: DomainConfig) {
	const areas = domainPathnames
		? [
				...new Map(
					Object.values(domainPathnames)
						.filter(isAppAreaConfig)
						.map((a) => [a.appArea, a]),
				).values(),
			]
		: APP_AREAS

	const allRoutes: Array<{
		path: string
		name: string
		component: () => Promise<unknown>
		meta?: { isLanding?: boolean }
	}> = []

	areas.forEach((appAreaConfig) => {
		const routes = generateRoutesFromAppArea(appAreaConfig)
		allRoutes.push(...routes)
	})

	return allRoutes
}

/**
 * Роуты точек входа в воронку (сейчас одна: `/app`).
 *
 * Каждая точка входа рендерит лендинг; целевая area резолвится из префикса пути
 * в рантайме (см. `getFunnelPrefixFromPath` + `pathnames` в funnels.ts), поэтому
 * сам компонент один и тот же — `LandingView`. Пути относительные: роуты
 * монтируются детьми `ScreenLayout` (parent `/`), как раньше делал лендинг `/`.
 */
export function entryPointRoutes() {
	return ENTRY_POINTS.map((entry) => ({
		path: entry.path.replace(/^\//, ''),
		name: entry.name,
		component: () => import('@/modules/Landing/LandingView.vue'),
		meta: {
			isLanding: true,
			hideHeader: true,
		},
	}))
}
